import { env } from '@/env/client';
import { SonioxClient } from '@/lib/soniox/client';
import type { SonioxError } from '@/lib/soniox/errors';
import {
  getTemporaryConfiguration,
  type SonioxTemporaryConfiguration,
  warmTemporaryConfiguration,
} from '@/lib/soniox/temp-api-key-cache';
import type { SpeechToTextAPIResponse } from '@/lib/soniox/types';
import type { TranscriptionToken } from '@/lib/transcription/types';
import { useConvexAction, useConvexMutation } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';
import { buildSonioxContextFromUserSettings } from '~/shared/soniox-user-context';

const AUDIO_SAMPLE_RATE_HZ = 44100;
const AUDIO_CHANNELS = 1;
const RESUME_CONTEXT_MAX_CHARS = 2000;

const buildResumeContext = (fullText: string) => {
  const trimmed = fullText.trim();
  if (trimmed.length <= RESUME_CONTEXT_MAX_CHARS) {
    return trimmed;
  }

  const sliced = trimmed.slice(-RESUME_CONTEXT_MAX_CHARS);
  const firstWhitespace = sliced.search(/\s/);

  if (firstWhitespace === -1) {
    return sliced;
  }

  return sliced.slice(firstWhitespace + 1).trim();
};

export interface SonioxClientCallbacks {
  onAudioPayload: (payload: ArrayBuffer) => void;
  onPartialResult: (result: SpeechToTextAPIResponse) => void;
  onError: (error: SonioxError) => void;
}

/**
 * Manages the Soniox WebSocket client, three-part text model
 * (initialText + finalText + nonFinalText), and autosave to Convex.
 */
export const useLiveTranscription = ({
  reportId,
}: {
  reportId: Id<'reports'>;
}) => {
  const [error, setError] = useState<SonioxError | null>(null);
  const [transcriptionState, setTranscriptionState] = useState({
    initialText: '',
    finalText: '',
    nonFinalText: '',
  });

  const clientRef = useRef<SonioxClient | null>(null);
  const latestFullTextRef = useRef<string>('');
  const lastSavedTextRef = useRef<string>('');
  const baseOffsetMsRef = useRef(0);
  const finalTokenBufferRef = useRef<TranscriptionToken[]>([]);
  const finalTokenReadIndexRef = useRef(0);
  const seenFinalTokenSignaturesRef = useRef<Set<string>>(new Set());

  const convex = useConvex();

  const { mutateAsync: getTemporaryApiKey } = useMutation({
    mutationFn: useConvexAction(api.transcriptionActions.getTemporaryApiKey),
  });

  const { mutateAsync: updateReport } = useMutation({
    mutationFn: useConvexMutation(api.reports.updateReportTranscription),
  });

  const fetchTemporaryConfiguration =
    useCallback(async (): Promise<SonioxTemporaryConfiguration> => {
      return await getTemporaryApiKey({});
    }, [getTemporaryApiKey]);

  useEffect(() => {
    void warmTemporaryConfiguration(fetchTemporaryConfiguration);
  }, [fetchTemporaryConfiguration]);

  const saveText = useCallback(
    async (force = false) => {
      const currentText = latestFullTextRef.current;
      const lastSaved = lastSavedTextRef.current;

      if (!reportId || (!force && currentText === lastSaved)) {
        return;
      }

      await updateReport({
        id: reportId,
        rawTranscription: currentText,
      });

      lastSavedTextRef.current = currentText;
    },
    [reportId, updateReport],
  );

  const init = useCallback((currentDbText: string, baseOffsetMs = 0) => {
    const initialText =
      currentDbText.length > 0 && !currentDbText.endsWith(' ')
        ? `${currentDbText} `
        : currentDbText;

    setTranscriptionState({
      initialText,
      finalText: '',
      nonFinalText: '',
    });

    latestFullTextRef.current = currentDbText;
    lastSavedTextRef.current = currentDbText;
    baseOffsetMsRef.current = baseOffsetMs;
    finalTokenBufferRef.current = [];
    finalTokenReadIndexRef.current = 0;
    seenFinalTokenSignaturesRef.current = new Set();
    setError(null);
  }, []);

  const createClient = useCallback(
    (callbacks: SonioxClientCallbacks) => {
      const client = new SonioxClient({
        model: env.VITE_SONIOX_RT_MODEL,
        languageHints: ['en', 'sl'],
        numChannels: AUDIO_CHANNELS,
        sampleRate: AUDIO_SAMPLE_RATE_HZ,
        configuration: async (options) =>
          await getTemporaryConfiguration(fetchTemporaryConfiguration, {
            forceRefresh: options?.forceRefresh,
          }),
        context: async () => {
          const userSettings = await convex.query(
            api.userSettings.getSettings,
            {},
          );
          const resumeContext = buildResumeContext(latestFullTextRef.current);
          return (
            buildSonioxContextFromUserSettings(userSettings, {
              resumeText: resumeContext,
            }) ?? {}
          );
        },
        onAudioPayload: callbacks.onAudioPayload,
        onPartialResult: (result: SpeechToTextAPIResponse) => {
          const tokens = result.tokens.filter(
            (token) => token.text !== '<end>',
          );

          const newFinalText = tokens
            .filter((token) => token.is_final)
            .map((token) => token.text)
            .join('');

          const newNonFinalText = tokens
            .filter((token) => !token.is_final)
            .map((token) => token.text)
            .join('');

          const newFinalTokens: TranscriptionToken[] = [];
          for (const token of tokens) {
            if (!token.is_final) continue;

            const relativeStartMs = Math.max(
              0,
              Math.round(token.start_ms ?? token.end_ms ?? 0),
            );
            const relativeEndMs = Math.max(
              relativeStartMs,
              Math.round(token.end_ms ?? token.start_ms ?? relativeStartMs),
            );
            const startMs = baseOffsetMsRef.current + relativeStartMs;
            const endMs = baseOffsetMsRef.current + relativeEndMs;
            const signature = `${startMs}|${endMs}|${token.text}`;

            if (seenFinalTokenSignaturesRef.current.has(signature)) continue;
            seenFinalTokenSignaturesRef.current.add(signature);

            newFinalTokens.push({
              text: token.text,
              startMs,
              endMs,
              confidence: token.confidence ?? 0,
              isFinal: true,
              speaker: token.speaker,
              language: token.language,
            });
          }

          if (newFinalTokens.length > 0) {
            finalTokenBufferRef.current.push(...newFinalTokens);
          }

          setTranscriptionState((prev) => {
            const nextSessionContent = prev.finalText + newFinalText;
            const fullText =
              prev.initialText + nextSessionContent + newNonFinalText;

            latestFullTextRef.current = fullText;

            return {
              ...prev,
              finalText: nextSessionContent,
              nonFinalText: newNonFinalText,
            };
          });

          callbacks.onPartialResult(result);
        },
        onError: callbacks.onError,
      });

      clientRef.current = client;
      return client;
    },
    [convex, fetchTemporaryConfiguration],
  );

  const startClient = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.start();
    }
  }, []);

  const stopClient = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.stop();
      clientRef.current = null;
    }
  }, []);

  const consolidateText = useCallback(() => {
    setTranscriptionState((prev) => {
      const finalFullText =
        prev.initialText + prev.finalText + prev.nonFinalText;

      latestFullTextRef.current = finalFullText;

      return {
        initialText: finalFullText,
        finalText: '',
        nonFinalText: '',
      };
    });
  }, []);

  const setBaseOffset = useCallback((ms: number) => {
    baseOffsetMsRef.current = ms;
  }, []);

  const consumeFinalTokensForRange = useCallback(
    (startMs: number, endMs: number) => {
      const tokens = finalTokenBufferRef.current;
      const consumed: TranscriptionToken[] = [];
      let cursor = finalTokenReadIndexRef.current;

      while (cursor < tokens.length) {
        const token = tokens[cursor];
        if (token.endMs > endMs) break;
        if (token.endMs >= startMs) {
          consumed.push(token);
        }
        cursor += 1;
      }

      finalTokenReadIndexRef.current = cursor;
      return consumed;
    },
    [],
  );

  const cleanup = useCallback(() => {
    clientRef.current?.stop();
    clientRef.current = null;
  }, []);

  return {
    error,
    setError,
    previousText: transcriptionState.initialText,
    currentSegmentText: transcriptionState.finalText,
    finalText: transcriptionState.initialText + transcriptionState.finalText,
    nonFinalText: transcriptionState.nonFinalText,
    init,
    setBaseOffset,
    createClient,
    startClient,
    stopClient,
    consolidateText,
    consumeFinalTokensForRange,
    saveText,
    cleanup,
  };
};
